// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useRef, useState, type JSX } from "react";
import { Body1, Button, Spinner, Subtitle2, makeStyles, tokens } from "@fluentui/react-components";

import {
  SIGN_IN_AGAIN_LABEL,
  SIGNED_OUT_WRITE_BODY,
} from "../../../components/common/signedOut/signedOutCopy";
import { useSignInAgain } from "../../../components/common/signedOut/useSignInAgain";
import { useAuthSession } from "../../../hooks/useAuthSession";
import { replaceHistoryEntry } from "../../../router/historyIndex";
import { navigateTo } from "../../../router/navigation";
import { useAuthGate } from "../../../routes/useAuthGate";
import { isSignedOutError } from "../../../services/api/demoData";
import {
  connectorSettingsUrl,
  type ConnectorCallbackConnector,
  type ConnectorCallbackEventDetail,
} from "../../../utils/connectorCallback";
import { OAUTH_POPUP_SOURCE, broadcastRelayMessage } from "../../../utils/oauthPopup";
import { completeConnection, connectionErrorCode } from "../api/connectionCompleteApi";
import {
  CONNECTOR_DISPLAY_NAMES,
  providerNotConnectedTitle,
} from "../components/providerConnectMessages";

const PENDING_KEY_PREFIX = "riddle.connectorCallback.";

const PENDING_TTL_MS = 10 * 60 * 1000;

const POSTED_KEY_PREFIX = "riddle.connectorCallback.posted.";

interface PendingCallback {
  code: string;
  state: string;
  signInStarted: boolean;
  savedAt: number;
}

const useStyles = makeStyles({
  page: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: tokens.spacingVerticalL,
    maxWidth: "400px",
    width: "100%",
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTopLeftRadius: tokens.borderRadiusXLarge,
    borderTopRightRadius: tokens.borderRadiusXLarge,
    borderBottomLeftRadius: tokens.borderRadiusXLarge,
    borderBottomRightRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    textAlign: "center",
  },
  logo: {
    width: "48px",
    height: "48px",
  },
  body: {
    color: tokens.colorNeutralForeground2,
  },
});

function pendingKey(connector: ConnectorCallbackConnector): string {
  return `${PENDING_KEY_PREFIX}${connector}`;
}

function postedKey(connector: ConnectorCallbackConnector): string {
  return `${POSTED_KEY_PREFIX}${connector}`;
}

function markPosted(connector: ConnectorCallbackConnector, state: string): void {
  try {
    window.sessionStorage.setItem(postedKey(connector), state);
  } catch {
  }
}

function unmarkPosted(connector: ConnectorCallbackConnector): void {
  try {
    window.sessionStorage.removeItem(postedKey(connector));
  } catch {
  }
}

function wasPosted(connector: ConnectorCallbackConnector, state: string): boolean {
  try {
    return window.sessionStorage.getItem(postedKey(connector)) === state;
  } catch {
    return false;
  }
}

function writePending(connector: ConnectorCallbackConnector, pending: PendingCallback): void {
  try {
    window.sessionStorage.setItem(pendingKey(connector), JSON.stringify(pending));
  } catch {
  }
}

function clearPending(connector: ConnectorCallbackConnector): void {
  try {
    window.sessionStorage.removeItem(pendingKey(connector));
  } catch {
  }
}

function readPending(connector: ConnectorCallbackConnector): PendingCallback | null {
  let stored: Partial<PendingCallback> | null = null;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(pendingKey(connector)) ?? "null") as
      | Partial<PendingCallback>
      | null;
  } catch {
    stored = null;
  }
  if (
    typeof stored?.code === "string" &&
    typeof stored.state === "string" &&
    typeof stored.signInStarted === "boolean" &&
    typeof stored.savedAt === "number" &&
    Date.now() - stored.savedAt < PENDING_TTL_MS
  ) {
    return {
      code: stored.code,
      state: stored.state,
      signInStarted: stored.signInStarted,
      savedAt: stored.savedAt,
    };
  }
  clearPending(connector);
  return null;
}

function capturePending(connector: ConnectorCallbackConnector): PendingCallback | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  replaceHistoryEntry(window.location.pathname);
  if (!code || !state) {
    return readPending(connector);
  }
  if (wasPosted(connector, state)) {
    return null;
  }
  const pending = { code, state, signInStarted: false, savedAt: Date.now() };
  writePending(connector, pending);
  return pending;
}

function sameOriginOpener(): Window | null {
  const opener = window.opener as Window | null;
  if (opener === null || opener === window) {
    return null;
  }
  try {
    return !opener.closed && opener.location.origin === window.location.origin ? opener : null;
  } catch {
    return null;
  }
}

function finishCallback(outcome: ConnectorCallbackEventDetail): void {
  const message =
    outcome.reason === undefined
      ? { source: OAUTH_POPUP_SOURCE, connector: outcome.connector }
      : { source: OAUTH_POPUP_SOURCE, connector: outcome.connector, error: outcome.reason };
  const opener = sameOriginOpener();
  if (opener === null) {
    broadcastRelayMessage(message);
    navigateTo(connectorSettingsUrl(outcome.connector, outcome), { replace: true });
  } else {
    opener.postMessage(message, window.location.origin);
  }
  window.close();
}

export interface ConnectionCallbackPageProps {
  connector: ConnectorCallbackConnector;
}

export function ConnectionCallbackPage({ connector }: ConnectionCallbackPageProps): JSX.Element {
  const styles = useStyles();
  const { isAuthorized, isProcessing, isCheckingSession } = useAuthGate();
  const { getAccessToken } = useAuthSession();
  const signInAgain = useSignInAgain();
  const [isSignedOut, setIsSignedOut] = useState(false);
  const pendingRef = useRef<PendingCallback | null | undefined>(undefined);
  const postedRef = useRef(false);
  const resumedAfterSignInRef = useRef(false);
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  });

  useEffect(() => {
    if (pendingRef.current === undefined) {
      pendingRef.current = capturePending(connector);
      resumedAfterSignInRef.current = pendingRef.current?.signInStarted === true;
    }
    const pending = pendingRef.current;
    if (pending === null) {
      navigateTo(connectorSettingsUrl(connector), { replace: true });
      return;
    }
    if (postedRef.current || isProcessing || isCheckingSession) {
      return;
    }
    if (!isAuthorized) {
      if (resumedAfterSignInRef.current) {
        setIsSignedOut(true);
      } else if (!pending.signInStarted) {
        pending.signInStarted = true;
        writePending(connector, pending);
        signInAgain();
      }
      return;
    }
    postedRef.current = true;
    clearPending(connector);
    markPosted(connector, pending.state);
    void (async () => {
      try {
        const token = await getAccessTokenRef.current().catch(() => undefined);
        await completeConnection(connector, pending, { token });
        finishCallback({ connector, status: "connected" });
      } catch (error) {
        if (isSignedOutError(error)) {
          writePending(connector, pending);
          unmarkPosted(connector);
          setIsSignedOut(true);
          return;
        }
        finishCallback({
          connector,
          status: "error",
          reason: connectionErrorCode(error) ?? "unknown",
        });
      }
    })();
  }, [connector, isAuthorized, isProcessing, isCheckingSession, signInAgain]);

  const name = CONNECTOR_DISPLAY_NAMES[connector];

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <img src="/riddle-brand-icon.png" alt="Riddle" className={styles.logo} />
        {isSignedOut ? (
          <>
            <Subtitle2 as="h1">{providerNotConnectedTitle(connector)}</Subtitle2>
            <Body1 className={styles.body}>{SIGNED_OUT_WRITE_BODY}</Body1>
            <Button appearance="primary" onClick={signInAgain}>
              {SIGN_IN_AGAIN_LABEL}
            </Button>
          </>
        ) : (
          <div role="status">
            <Spinner size="large" label={`Connecting ${name}…`} labelPosition="below" />
            <Body1 className={styles.body}>This only takes a moment. Keep this window open.</Body1>
          </div>
        )}
      </section>
    </main>
  );
}
